import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const APP_STATUSES = ["Yeni Başvuru", "İnceleniyor", "Telefonla Görüşülecek", "Evrak Bekleniyor", "Uygun Bulundu", "Yedek Aday", "Olumsuz", "İşe Alındı"];
export const CB_STATUSES = ["Yeni", "Geri Aranacak", "Görüşüldü", "Başvuruya Dönüştü", "Tamamlandı"];
export const CARD = { silahli: "Silahlı", silahsiz: "Silahsız", yok: "Kartı yok" };
export const DOC_KINDS = { photo: "Fotoğraf", card_front: "Kimlik Kartı Ön Yüz", card_back: "Kimlik Kartı Arka Yüz", cv: "Özgeçmiş", other: "Diğer Belge" };
const CLS = {
  "Yeni Başvuru": "bg-red-100 text-red-800", Yeni: "bg-red-100 text-red-800", "İnceleniyor": "bg-blue-100 text-blue-800",
  "Telefonla Görüşülecek": "bg-amber-100 text-amber-800", "Geri Aranacak": "bg-amber-100 text-amber-800", "Evrak Bekleniyor": "bg-orange-100 text-orange-800",
  "Uygun Bulundu": "bg-emerald-100 text-emerald-800", "Görüşüldü": "bg-blue-100 text-blue-800", "Yedek Aday": "bg-violet-100 text-violet-800",
  Olumsuz: "bg-zinc-200 text-zinc-700", "İşe Alındı": "bg-zinc-900 text-white", "Başvuruya Dönüştü": "bg-emerald-100 text-emerald-800",
  Tamamlandı: "bg-zinc-200 text-zinc-700", Eksik: "bg-orange-100 text-orange-800", Tamam: "bg-emerald-100 text-emerald-800",
};

export function Badge({ value, testid }) {
  return <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${CLS[value] || "bg-zinc-100 text-zinc-700"}`} data-testid={testid}>{value || "-"}</span>;
}

export const fmtDay = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("tr-TR") : "-");

export function expirySoon(d) {
  if (!d) return false;
  return (new Date(`${d}T00:00:00`) - new Date()) / 86400000 <= 60;
}

export async function download(url, name, { method = "get", data, params } = {}) {
  try {
    const r = await api.request({ url, method, data, params, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    return true;
  } catch (e) {
    let msg = "İndirme başarısız";
    try {
      msg = JSON.parse(await e.response.data.text()).detail || msg;
    } catch (parseErr) {
      msg = e.message || msg;
    }
    toast.error(msg);
    return false;
  }
}

export function useList(url, params) {
  const [rows, setRows] = useState(null);
  const key = JSON.stringify(params);
  const load = useCallback(() => {
    api.get(url, { params: JSON.parse(key) }).then((r) => setRows(r.data)).catch((e) => { toast.error(errMsg(e)); setRows([]); });
  }, [url, key]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  return [rows, load];
}

export function copyText(t) {
  const fallback = () => {
    const ta = document.createElement("textarea");
    ta.value = t;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    if (ok) toast.success("Kopyalandı");
    else toast.error("Kopyalanamadı");
  };
  if (!navigator.clipboard) return fallback();
  navigator.clipboard.writeText(t).then(() => toast.success("Kopyalandı"), fallback);
}

export function Section({ title, children, testid, action }) {
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-zinc-900">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Empty({ text = "Kayıt bulunamadı.", testid }) {
  return <p className="rounded-lg border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500" data-testid={testid}>{text}</p>;
}

export function Listing({ rows, empty, children }) {
  if (!rows) return <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-[color:var(--red)]" />;
  if (rows.length === 0) return empty;
  return children;
}
